import { Injectable } from '@angular/core';
import { IMessage } from '../models/chatbot.interfaces';
import { ChatbotStateModel } from '../state/chatbot.state';

export const CHATBOT_TIME_CONFIG = {
  FLUSH_MS: 50,
  GAP_GRACE_MS: 400,
  MAX_SEQUENCE_NUMBER: Number.MAX_SAFE_INTEGER,
  TICK_MS: 50, // how often to reveal
  STEP_CHARS: 3, // how many chars per tick (1–3 natural)
  MAX_PENDING_CHUNK_AGE_MS: 30000
};

@Injectable({
  providedIn: 'root'
})
export class ChatbotWebsocketConversationHelperService {
  // Small normalizer
  public normalizeChunk(raw: string): string {
    const fixed = this.characterFixes(raw);
    return this.stripControls(fixed);
  }

  // Replace common mojibakes
  public characterFixes(s: string): string {
    // em dash
    s = s.replace(/\u00E2\u0080\u0094/g, '—'); // "â" → "—"
    // en dash
    s = s.replace(/\u00E2\u0080\u0093/g, '–');
    // ellipsis
    s = s.replace(/\u00E2\u0080\u00A6/g, '…'); // ellipsis
    // left/right quotes
    s = s.replace(/\u00E2\u0080\u009C/g, '“').replace(/\u00E2\u0080\u009D/g, '”');
    s = s.replace(/\u00E2\u0080\u0098/g, '‘').replace(/\u00E2\u0080\u0099/g, '’');
    s = s.replace(/\u00E2\u0080\u00A2/g, '•');
    return s;
  }

  public createPlaceholderMessage(messageList: IMessage[]): { tempId: number; placeholder: IMessage } {
    const now = new Date();
    const maxId = messageList.length ? Math.max(...messageList.map((m) => m.id || 0)) : 0;
    const tempId = maxId + 1;

    return {
      tempId,
      placeholder: {
        id: tempId,
        type: 'ASSISTANT',
        content: '',
        status: 'streaming',
        createdDate: now
      }
    };
  }

  public upsertMessageById(message: IMessage, messages: IMessage[], patch: Partial<IMessage>): IMessage[] {
    const idx = messages.findIndex((m) => m.id === message.id);
    if (idx === -1) return messages;

    const updated = { ...messages[idx], ...patch };
    const next = messages.slice();
    next[idx] = updated;
    return next;
  }

  public appendToMessageContent(id: number, messages: IMessage[], text: string): IMessage[] {
    const idx = messages.findIndex((m) => m.id === id);
    if (idx === -1) return messages;

    const curr = messages[idx];
    const next = messages.slice();
    next[idx] = { ...curr, content: (curr.content || '') + text };
    return next;
  }

  public cleanupOldPendingChunks(
    stream: ChatbotStateModel['stream'],
    maxAgeMs: number = CHATBOT_TIME_CONFIG.MAX_PENDING_CHUNK_AGE_MS
  ): {
    pending: Record<number, { content: string; timestamp: number }>;
    missingSince: Record<number, number>;
    changed: boolean;
  } {
    if (!stream?.active || !stream.pending) {
      return { pending: {}, missingSince: {}, changed: false };
    }

    const now = Date.now();
    const pending = { ...stream.pending };
    const missingSince = { ...(stream.missingSince || {}) };
    let changed = false;

    // Remove chunks that are too old
    Object.keys(pending).forEach((seqStr) => {
      const seq = parseInt(seqStr, 10);
      const chunk = pending[seq];

      // Skip if it's the next expected sequence or within a small window after it
      if (seq >= stream.nextSeq && seq < stream.nextSeq + 10) return;

      // Remove if too old
      if (now - chunk.timestamp > maxAgeMs) {
        delete pending[seq];
        changed = true;

        if (missingSince[seq]) {
          delete missingSince[seq];
        }
      }
    });

    return { pending, missingSince, changed };
  }

  public processStreamChunks(
    stream: ChatbotStateModel['stream'],
    grace: number = CHATBOT_TIME_CONFIG.GAP_GRACE_MS
  ): {
    buffer: string;
    nextSeq: number;
    pending: Record<number, { content: string; timestamp: number }>;
    missingSince: Record<number, number>;
    changed: boolean;
  } {
    if (!stream) {
      return { buffer: '', nextSeq: 0, pending: {}, missingSince: {}, changed: false };
    }

    let changed = false;
    let buffer = stream.buffer || '';
    let nextSeq = stream.nextSeq;
    const pending = { ...(stream.pending || {}) };
    const missingSince = { ...(stream.missingSince || {}) };
    const now = Date.now();

    // Add safety counters to prevent infinite loops
    let outerLoopCount = 0;
    const MAX_LOOP_ITERATIONS = 1000; // Adjust based on expected maximum sequence count

    // Process any pending chunks that are ready
    while (pending[nextSeq] !== undefined && outerLoopCount < MAX_LOOP_ITERATIONS) {
      buffer += pending[nextSeq].content;
      delete pending[nextSeq];
      nextSeq++;
      changed = true;
      outerLoopCount++;
    }

    // Reset counter for the second loop
    outerLoopCount = 0;

    // Promote past expired gaps
    while (missingSince[nextSeq] && now - missingSince[nextSeq] > grace && outerLoopCount < MAX_LOOP_ITERATIONS) {
      delete missingSince[nextSeq];
      nextSeq++;
      changed = true;

      let innerLoopCount = 0;
      while (pending[nextSeq] !== undefined && innerLoopCount < MAX_LOOP_ITERATIONS) {
        buffer += pending[nextSeq].content;
        delete pending[nextSeq];
        nextSeq++;
        changed = true;
        innerLoopCount++;
      }

      outerLoopCount++;
    }

    return { buffer, nextSeq, pending, missingSince, changed };
  }

  public shouldErrorOnCompletion(stream: ChatbotStateModel['stream'], maxMissingSequences = 20): boolean {
    if (!stream?.active || !stream.finishing) {
      return false;
    }

    // If still waiting for the next sequence and there are missing sequences
    if (stream.missingSince && Object.keys(stream.missingSince).length > 0) {
      // Check if we're missing a large number of consecutive sequences
      const missingSeqs = Object.keys(stream.missingSince)
        .map((seq) => parseInt(seq, 10))
        .sort((a, b) => a - b);

      // Missing more than maxMissingSequences
      if (missingSeqs.length >= maxMissingSequences) {
        console.error(`Stream completion error: Missing ${missingSeqs.length} sequences`, missingSeqs);
        return true;
      }

      // Check for large gaps in sequence numbers
      const largestGap = this.findLargestSequenceGap(stream);
      if (largestGap >= maxMissingSequences) {
        console.error(`Stream completion error: Large gap of ${largestGap} sequences detected`);
        return true;
      }
    }

    return false;
  }

  public createInitialStreamState(flushMs?: number): ChatbotStateModel['stream'] {
    return {
      active: false,
      messageId: null,
      serverMessageId: null,
      buffer: '',
      finishing: false,
      lastFlushTs: 0,
      flushMs: flushMs ?? CHATBOT_TIME_CONFIG.FLUSH_MS,
      nextSeq: 0,
      pending: {},
      missingSince: {},
      pendingTimestamps: {},
      gapGraceMs: CHATBOT_TIME_CONFIG.GAP_GRACE_MS
    };
  }

  private findLargestSequenceGap(stream: ChatbotStateModel['stream']): number {
    if (!stream?.missingSince) return 0;

    const missingSeqs = Object.keys(stream.missingSince)
      .map((seq) => parseInt(seq, 10))
      .sort((a, b) => a - b);
    if (missingSeqs.length <= 1) return missingSeqs.length;

    let largestGap = 0;
    for (let i = 1; i < missingSeqs.length; i++) {
      const gap = missingSeqs[i] - missingSeqs[i - 1];
      if (gap > largestGap) {
        largestGap = gap;
      }
    }

    // Check the gap from the current nextSeq to the first missing sequence
    if (missingSeqs.length > 0 && stream.nextSeq !== null) {
      const initialGap = missingSeqs[0] - stream.nextSeq;
      if (initialGap > largestGap) {
        largestGap = initialGap;
      }
    }

    return largestGap;
  }

  // Strip non-printable control chars (keep \n \r \t)
  private stripControls(s: string): string {
    return s.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');
  }
}
